"use client";

import { useRouter } from "next/navigation";
import { useForm } from "@/lib/hooks/use-form";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface RegisterFormValues {
  email: string;
  password: string;
  slug: string;
  displayName: string;
  phoneNumber: string;
  country: string;
}

function DotRow() {
  return (
    <div className="flex flex-wrap gap-[7px]">
      {Array.from({ length: 40 }).map((_, i) => (
        <span key={i} className="h-1 w-1 rounded-full bg-[#C9C7B9]" />
      ))}
    </div>
  );
}

const SUPPORTERS: [number, number, string][] = [
  [372, 138, "#EBEDFC"],
  [344, 300, "#E7F3EA"],
  [90, 138, "#FCF1DC"],
];

export default function InscriptionPage() {
  const router = useRouter();
  const { values, update, error, loading, submit } = useForm<RegisterFormValues>({
    email: "",
    password: "",
    slug: "",
    displayName: "",
    phoneNumber: "",
    country: "BF",
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await submit(async () => {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error ?? "Erreur lors de l\'inscription");
      }

      router.push("/connexion?inscrit=1");
    });
  }

  return (
    <div className="flex min-h-screen bg-white">
      <div className="hidden w-[46%] max-w-[560px] flex-col items-center justify-center gap-8 bg-panel px-10 py-10 lg:flex">
        <DotRow />

        <div className="flex flex-col items-center gap-6">
          <svg width="370" height="330" viewBox="0 0 460 400">
            <circle cx="230" cy="200" r="172" fill="none" stroke="#DEDCCF" strokeWidth="1.5" strokeDasharray="1,7" />

            <line x1="230" y1="205" x2="230" y2="48" stroke="#DEDCCF" strokeWidth="2" strokeDasharray="1,6" strokeLinecap="round" />
            <line x1="230" y1="205" x2="372" y2="138" stroke="#DEDCCF" strokeWidth="2" strokeDasharray="1,6" strokeLinecap="round" />
            <line x1="230" y1="205" x2="344" y2="300" stroke="#DEDCCF" strokeWidth="2" strokeDasharray="1,6" strokeLinecap="round" />
            <line x1="230" y1="205" x2="90" y2="138" stroke="#DEDCCF" strokeWidth="2" strokeDasharray="1,6" strokeLinecap="round" />

            {SUPPORTERS.map(([cx, cy, bg], i) => (
              <g key={i} transform={"translate(" + cx + "," + cy + ")"}>
                <circle r="18" fill={bg} />
                <circle cx="0" cy="-3" r="5.4" fill="#FFFFFF" />
                <path d="M-8,10 C-8,2 -4.5,-1 0,-1 C4.5,-1 8,2 8,10 Z" fill="#FFFFFF" />
              </g>
            ))}

            <g transform="translate(230,48)">
              <circle r="23" fill="#FBEAE0" />
              <circle cx="0" cy="-3" r="6.6" fill="#EB6D3B" />
              <path d="M-10,12 C-10,2 -5.6,-1 0,-1 C5.6,-1 10,2 10,12 Z" fill="#EB6D3B" />
              <circle cx="15" cy="-16" r="9" fill="#FCF1DC" />
              <path d="M12,-16 L18,-16 M15,-19 L15,-13" stroke="#EB6D3B" strokeWidth="2.2" strokeLinecap="round" />
            </g>

            <g transform="translate(230,208)">
              <rect x="-54" y="-97" width="108" height="194" rx="24" fill="#FFFFFF" stroke="#E3E1D6" strokeWidth="2" />
              <rect x="-40" y="-80" width="80" height="142" rx="12" fill="#F5F4EE" />
              <rect x="-12" y="-90" width="24" height="5" rx="2.5" fill="#DEDCCF" />
              <g transform="translate(0,18)">
                <path d="M0,24 V-4" stroke="#4C9A6A" strokeWidth="4" strokeLinecap="round" />
                <path d="M0,-4 C-13,-7 -17,-18 -11,-26 C-2,-22 1,-12 0,-4 Z" fill="#4C9A6A" />
                <path d="M0,-4 C13,-7 17,-18 11,-26 C2,-22 -1,-12 0,-4 Z" fill="#6FB585" />
              </g>
            </g>
          </svg>

          <div className="flex flex-col items-center gap-3">
            <p className="max-w-[400px] text-center text-[22px] font-bold leading-tight text-ink">
              Rejoignez les createurs soutenus chaque jour.
            </p>
            <p className="max-w-[340px] text-center text-[14px] leading-relaxed text-muted">
              Recevez des dons directement en Mobile Money, sans compte bancaire.
            </p>
          </div>
        </div>

        <DotRow />
      </div>

      <div className="flex flex-1 flex-col justify-center overflow-auto px-8 py-12 sm:px-16 lg:px-24">
        <div className="mx-auto w-full max-w-[400px]">
          <div className="mb-7 flex items-center gap-2.5">
            <svg width="28" height="28" viewBox="0 0 30 30">
              <rect width="30" height="30" rx="9" fill="#EB6D3B" />
              <path d="M15,20 C10,16.5 7,13.8 7,10.4 C7,7.9 9,6 11.4,6 C13,6 14.4,6.9 15,8.3 C15.6,6.9 17,6 18.6,6 C21,6 23,7.9 23,10.4 C23,13.8 20,16.5 15,20 Z" fill="#FFFFFF" />
            </svg>
            <span className="text-[19px] font-extrabold tracking-tight text-ink">mercilaba</span>
          </div>

          <h1 className="mb-1.5 text-[27px] font-extrabold tracking-tight text-ink">Creer votre page createur</h1>
          <p className="mb-6 text-[14.5px] text-muted">Quelques informations pour demarrer.</p>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <label htmlFor="name" className="mb-1.5 block text-[13px] font-semibold text-ink">Nom affiche</label>
              <Input id="name" type="text" placeholder="Aicha Traore" value={values.displayName} onChange={(e) => update("displayName", e.target.value)} required />
            </div>

            <div>
              <label htmlFor="slug" className="mb-1.5 block text-[13px] font-semibold text-ink">Votre page</label>
              <div className="flex items-center overflow-hidden rounded-xl border-[1.5px] border-line bg-[#FBFAF6] focus-within:border-accent">
                <span className="whitespace-nowrap py-3 pl-3.5 pr-1.5 font-mono text-[13px] text-placeholder">mercilaba.site/</span>
                <input
                  id="slug"
                  type="text"
                  placeholder="aicha-traore"
                  value={values.slug}
                  onChange={(e) => update("slug", e.target.value.toLowerCase())}
                  className="w-full bg-transparent py-3 pr-3 font-mono text-[13px] text-ink outline-none placeholder:text-placeholder"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email2" className="mb-1.5 block text-[13px] font-semibold text-ink">Email</label>
              <Input id="email2" type="email" placeholder="vous@exemple.com" value={values.email} onChange={(e) => update("email", e.target.value)} required />
            </div>

            <div>
              <label htmlFor="password2" className="mb-1.5 block text-[13px] font-semibold text-ink">Mot de passe</label>
              <Input id="password2" type="password" placeholder="8 caracteres min." value={values.password} onChange={(e) => update("password", e.target.value)} required />
            </div>

            <div>
              <label htmlFor="phone" className="mb-1.5 block text-[13px] font-semibold text-ink">Numero Mobile Money</label>
              <Input id="phone" type="tel" placeholder="22670000000" value={values.phoneNumber} onChange={(e) => update("phoneNumber", e.target.value)} required />
            </div>

            <Alert variant="error">{error}</Alert>

            <div className="pt-1">
              <Button type="submit" loading={loading} loadingText="Creation...">Creer mon compte</Button>
            </div>
          </form>

          <p className="mt-6 text-center text-[13.5px] text-muted">
            Deja un compte ?{" "}
            <a href="/connexion" className="font-bold text-ink underline decoration-line underline-offset-4">Se connecter</a>
          </p>
        </div>
      </div>
    </div>
  );
}
